/**
 * Contextual help for step pages and the workshop on-ramp.
 * Use **double asterisks** in step strings to bold in-app control names.
 * Text lives in the i18n catalogs (step.help.*); build sections at render time.
 */

import { t } from '../i18n/translate';

export interface HelpMenuItem {
  label: string;
  description: string;
}

export interface HelpSectionContent {
  title: string;
  description: string;
  details?: string;
  steps?: string[];
  menuItems?: HelpMenuItem[];
}

const MENU_ITEMS = [
  'profile',
  'invite',
  'feedback',
  'help',
  'start_over',
  'backup',
  'about',
  'privacy',
  'sign_out',
] as const;

export function getAppMenuHelp(): HelpSectionContent {
  return {
    title: t('step.help.menu.title'),
    description: t('step.help.menu.description'),
    menuItems: MENU_ITEMS.map((item) => ({
      label: t(`nav.menu.${item}`),
      description: t(`step.help.menu.${item}`),
    })),
  };
}

function stepWorkSteps(): string[] {
  return [
    t('step.help.work.step_actions'),
    t('step.help.work.step_help_button'),
    t('step.help.work.step_share'),
    t('step.help.work.step_field_help'),
  ];
}

export function getAppStepWorkHelp(): HelpSectionContent {
  return {
    title: t('step.help.work.title'),
    description: t('step.help.work.description'),
    steps: [t('step.help.work.step_open_steps'), ...stepWorkSteps()],
  };
}

export function getStepsTabHelp(): HelpSectionContent {
  return {
    title: t('step.help.steps_tab.title'),
    description: t('step.help.steps_tab.description'),
    steps: [
      t('step.help.steps_tab.step_start'),
      t('step.help.steps_tab.step_statuses'),
      t('step.help.steps_tab.step_auto'),
    ],
  };
}

export function getAppStepWorkOnTabHelp(): HelpSectionContent {
  return {
    title: t('step.help.work.title'),
    description: t('step.help.work.description'),
    steps: stepWorkSteps(),
  };
}

export function getWorkshopPageHelp(): HelpSectionContent {
  return {
    title: t('step.help.workshop.title'),
    description: t('step.help.workshop.description'),
    steps: [
      t('step.help.workshop.step_zoom'),
      t('step.help.workshop.step_steps'),
      t('step.help.workshop.step_first_step'),
      t('step.help.workshop.step_participants'),
      t('step.help.workshop.step_share_invite'),
      t('step.help.workshop.step_resources'),
      t('step.help.workshop.step_calendar'),
      t('step.help.workshop.step_preferences'),
      t('step.help.workshop.step_leave'),
    ],
  };
}

export function getWorkshopStepWorkHelp(): HelpSectionContent {
  return {
    title: t('step.help.work.title'),
    description: t('step.help.workshop_work.description'),
    steps: [
      t('step.help.workshop_work.step_open_steps'),
      t('step.help.workshop_work.step_actions'),
      t('step.help.work.step_help_button'),
      t('step.help.work.step_share'),
    ],
  };
}

export type StepHelpKey = 'app' | 'workshop' | 'steps-tab';

export function getStepHelpPanelTitle(stepKey: StepHelpKey): string {
  if (stepKey === 'workshop') return t('step.help.panel_title_workshop');
  return t('step.help.panel_title_app');
}
